import { useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { CircleCheck, CircleX, Clock, Landmark } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import { CheckboxFormField, DateFormField, defineEntitySource, EntityPickerField, MaskedFormField, SelectFormField, TextareaFormField, TextFormField } from '@/shared/form';
import { formatDocument } from '@/shared/format/document';
import { resolvePatternText } from '@/shared/patterns';
import { bankCompeSource, bankOptionSchema, listBankOptions } from '@/shared/reference';
import { useFieldConfig } from '@/shared/ui/field-config';

import { legacyBankAccountLabel, legacyCents, legacyDate } from '../credit-note.legacy-format';
import {
  approveState,
  clearedForOperation,
  MASKED_PIX_KEYS,
  LEGAL_ACCOUNT_TYPES,
  LEGAL_PIX_KEY_TYPES,
  NATURAL_ACCOUNT_TYPES,
  OPERATION_TYPES,
  PIX,
  PIX_AGENCY_AND_ACCOUNT,
  PIX_KEY_TYPES,
  SCHEDULE_ACTION_RESULT,
  SCHEDULE_ACTIONS,
  SCHEDULE_FAILED,
  SCHEDULE_NO_ACTION,
  scheduleBeneficiary,
  scheduleLabel,
  TRANSFER,
  TRANSITORY_ACTIONS,
  TRANSITORY_AUTOMATIC,
  type ApproveScheduleValues,
  type BankDetailsValues,
  type CloseTransitoryValues,
  type CosifOption,
  type LiquidationOption,
  type ScheduleAction,
  type ScheduleFacts,
  type ScheduleItem,
} from '../credit-note.liquidation';
import { cosifTransferAccountsQuery } from '../credit-note.queries';
import type { CreditNote } from '../credit-note.wire';
import { DialogSection } from './bar-fields';

// Campos dos diálogos da trilha "liquidação": o roteiro de liquidação (itens com valor, forma e
// beneficiário, que é a prévia legível), o encerramento e a revisão da conta transitória.

const TONE_CLASS: Readonly<Record<'ok' | 'err' | 'accent' | 'warn', string>> = {
  ok: 'text-ok',
  err: 'text-err',
  accent: 'text-accent-text',
  warn: 'text-warn',
};
const TONE_ICON = { ok: CircleCheck, err: CircleX, accent: Landmark, warn: Clock } as const;

/** Um item do roteiro como o cartão do legado resume: forma, dado, valor, vencimento e beneficiário. */
export function ScheduleItemSummary({ item, children, footer }: { readonly item: ScheduleItem; readonly children?: React.ReactNode; readonly footer?: React.ReactNode }) {
  const { i18n, t } = useLingui();
  const nd = t`N/D`;
  const label = scheduleLabel(item, nd);
  const beneficiary = scheduleBeneficiary(item);
  const due = legacyDate(item.informedDueDate ?? item.retrievedData?.bankslipData?.dueDate, i18n.locale);
  const document = beneficiary.document ? (formatDocument(beneficiary.document) ?? beneficiary.document) : null;
  return (
    <article className="flex flex-col gap-3 rounded-card border border-border bg-card p-3" aria-label={label.method ? i18n._(label.method) : nd}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <h4 className="text-sm font-semibold">{label.method ? i18n._(label.method) : nd}</h4>
        <span className="font-mono text-sm font-semibold tabular-nums">{legacyCents(item.amountInCents, i18n.locale)}</span>
      </div>
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
        <dt className="text-muted-foreground">{label.kind ? i18n._(label.kind) : t`Dados`}</dt>
        <dd className="min-w-0 break-words font-mono">{label.detail || nd}</dd>
        <dt className="text-muted-foreground">{t`Beneficiário`}</dt>
        <dd className="min-w-0 break-words">{[beneficiary.name, document].filter(Boolean).join(' · ') || nd}</dd>
        <dt className="text-muted-foreground">{t`Vencimento`}</dt>
        <dd>{due ?? nd}</dd>
        {item.statusDisplay ? (
          <>
            <dt className="text-muted-foreground">{t`Status`}</dt>
            <dd>{item.statusDisplay}</dd>
          </>
        ) : null}
        {item.failureMessage || item.erroMessage ? (
          <>
            <dt className="text-muted-foreground">{t`Motivo`}</dt>
            <dd className="min-w-0 break-words">{item.failureMessage || item.erroMessage}</dd>
          </>
        ) : null}
      </dl>
      {children}
      {footer}
    </article>
  );
}

function ActionResult({ action }: { readonly action: ScheduleAction | null }) {
  const { i18n } = useLingui();
  const result = SCHEDULE_ACTION_RESULT[action ?? SCHEDULE_NO_ACTION];
  const Icon = TONE_ICON[result.tone];
  return (
    <p className={`flex items-center gap-1.5 text-xs font-medium ${TONE_CLASS[result.tone]}`}>
      <Icon aria-hidden className="size-4" />
      {i18n._(result.label)}
    </p>
  );
}

const options = (i18n: Parameters<typeof resolvePatternText>[0], list: readonly LiquidationOption[]) => list.map((option) => ({ value: option.value, label: i18n._(option.label) }));
/** `sortToSelectOptionList` (helpers/formats/ArrayFormats.ts:10-25): pelo rótulo em maiúsculas. */
const sortedCosif = (cosif: readonly CosifOption[]) =>
  cosif.map((item) => ({ value: item.code, label: item.description })).sort((a, b) => (a.label.toUpperCase() < b.label.toUpperCase() ? -1 : a.label.toUpperCase() > b.label.toUpperCase() ? 1 : 0));

export interface ScheduleFieldsProps {
  readonly facts: ScheduleFacts;
  readonly cosif: readonly CosifOption[];
}

/** O drawer "Aprovações de roteiro de liquidação" (ApproveLiquidationScheduleWrapperForm.tsx + Form.tsx). */
export function ApproveScheduleFields({ facts, cosif }: ScheduleFieldsProps) {
  const { i18n, t } = useLingui();
  const { control, setValue, getValues } = useFormContext<ApproveScheduleValues>();
  const principal = useWatch({ control, name: 'cosifAccountCode' });
  const items = useWatch({ control, name: 'liquidationSchedule' });
  const state = approveState(facts, cosif, { cosifAccountCode: principal });
  const cosifOptions = sortedCosif(cosif);
  const transitoryOptions = options(i18n, TRANSITORY_ACTIONS).filter((option) => option.value !== TRANSITORY_AUTOMATIC || state.transitoryAutomatic);
  return (
    <>
      {facts.hasUnlinkedTransfer ? null : (
        <DialogSection title={t`Conta transitória`}>
          <SelectFormField<ApproveScheduleValues>
            name="cosifAccountCode"
            label={t`Conta de débito`}
            required
            placeholder={t`Selecione`}
            options={cosifOptions}
            // Container.tsx:104-120: COSIF sem conta externa tira a liquidação automática escolhida.
            onValueChange={(value) => {
              if (!state.external(value) && getValues('transitoryBankAccountAction') === TRANSITORY_AUTOMATIC) setValue('transitoryBankAccountAction', null);
            }}
          />
          {facts.hasTransitoryAccount ? <SelectFormField<ApproveScheduleValues> name="transitoryBankAccountAction" label={t`Ação`} required placeholder={t`Selecione`} options={transitoryOptions} /> : null}
        </DialogSection>
      )}
      <DialogSection title={t`Roteiro de liquidação`}>
        {facts.pending.length === 0 ? <p className="text-sm text-muted-foreground">{t`Nenhum pagamento novo e vinculado neste roteiro.`}</p> : null}
        {facts.pending.map((item, index) => {
          const values = items[index];
          const actionOptions = options(i18n, SCHEDULE_ACTIONS).filter((option) => option.value !== 'AutomaticLiquidation' || state.itemAutomatic(values?.cosifAccountCode ?? null));
          return (
            <ScheduleItemSummary key={item.id} item={item} footer={<ActionResult action={values?.action ?? null} />}>
              <div className="flex flex-col gap-3">
                {state.showItemCosif ? <SelectFormField<ApproveScheduleValues> name={`liquidationSchedule.${index}.cosifAccountCode`} label={t`Conta de débito`} required placeholder={t`Selecione`} options={cosifOptions} /> : null}
                <SelectFormField<ApproveScheduleValues> name={`liquidationSchedule.${index}.action`} label={t`Ação`} required options={actionOptions} />
                {values?.action === SCHEDULE_FAILED ? <TextareaFormField<ApproveScheduleValues> name={`liquidationSchedule.${index}.failureMessage`} label={t`Observações`} required rows={3} /> : null}
              </div>
            </ScheduleItemSummary>
          );
        })}
      </DialogSection>
    </>
  );
}

/** A conta que o `GET` traz, pelo rótulo do select do legado (`formatBankAccountForOption`). */
export function AccountLine({ label, account }: { readonly label: string; readonly account: unknown }) {
  const { i18n, t } = useLingui();
  const text = legacyBankAccountLabel(i18n, account);
  return (
    <p className="text-sm">
      <span className="text-muted-foreground">{label}: </span>
      <span className="font-mono">{text ?? t`N/D`}</span>
    </p>
  );
}

/** O modal "Encerrar conta transitória" (LiquidationScheduleClosureForm.tsx): só COSIF com conta externa. */
export function CloseTransitoryFields({ note }: { readonly note: CreditNote }) {
  const { t } = useLingui();
  const accounts = useQuery(cosifTransferAccountsQuery());
  const credit = (accounts.data ?? []).filter((item) => !!item.externalAccountId).map((item) => ({ value: item.code, label: item.description }));
  const transitory = (note as Record<string, unknown>)['transitoryBankAccount'];
  return (
    <>
      <AccountLine label={t`Conta transitória`} account={transitory} />
      <DateFormField<CloseTransitoryValues> name="paymentDate" label={t`Data de pagamento`} required />
      <SelectFormField<CloseTransitoryValues> name="cosifAccountCode" label={t`Conta de crédito`} required placeholder={accounts.isPending ? t`Carregando…` : t`Selecione`} options={credit} />
      <CheckboxFormField<CloseTransitoryValues> name="closeTransitoryBankAccount" label={t`Encerrar conta transitória`} />
    </>
  );
}

type BankItem = z.infer<typeof bankOptionSchema>;
/**
 * `useBankList` (contexts/bankList.tsx:18-68): a TED usa o `bankCompeSource` de `shared/reference` (código
 * inválido vira ISPB); o Pix por agência e conta, a mesma chamada com `compe: false` e o mesmo mapper.
 */
export const BANKS_ALL = defineEntitySource<BankItem>({
  key: ['reference', 'bank', 'all'],
  search: async (query, signal) => (await listBankOptions(query || undefined, signal, false)).data,
  toOption: bankCompeSource.toOption,
});

export interface BankDetailsFieldsProps {
  /** A conta atual, mostrada antes dos campos (revisão da conta transitória). Sem ela, a linha some. */
  readonly current?: unknown;
  readonly hideCurrent?: boolean;
  /** `isLegalPerson` do `BankDetails`: CNPJ no lugar do CPF e os tipos de conta de PJ (BankDetails.tsx:53-71). */
  readonly legalPerson?: boolean;
}

/** O `BankDetails` (pages/BankAccount/BankDetailsTab/BankDetails.tsx) com TED e Pix, como a revisão da conta transitória abre. */
export function BankDetailsFields({ current, hideCurrent = false, legalPerson = false }: BankDetailsFieldsProps) {
  const { i18n, t } = useLingui();
  const { control, setValue } = useFormContext<BankDetailsValues>();
  const operation = useWatch({ control, name: 'operationTypeValue' });
  const keyType = useWatch({ control, name: 'pixKeyTypeValue' });
  const operationConfig = useFieldConfig('operationTypeValue').options;
  const typeConfig = useFieldConfig('type').options;
  const fromConfig = (list: readonly unknown[] | undefined) =>
    list?.map((item) => {
      const option = item as { value?: string | number; label?: string };
      return { value: option.value === undefined ? '' : String(option.value), label: option.label ?? '' };
    });
  const clear = (patch: Partial<BankDetailsValues>) => {
    for (const [key, value] of Object.entries(patch)) setValue(key as keyof BankDetailsValues, value as never);
  };
  const isTransfer = operation === TRANSFER;
  const bankData = isTransfer || keyType === PIX_AGENCY_AND_ACCOUNT;
  const mask = keyType ? MASKED_PIX_KEYS[keyType] : undefined;
  const keyLabel = keyType === 'Phone' ? t`Telefone` : keyType === 'NaturalRegistrationNumber' ? t`CPF` : keyType === 'LegalRegistrationNumber' ? t`CNPJ` : keyType === 'Email' ? t`Email` : t`Chave aleatória`;
  return (
    <>
      {hideCurrent ? null : <AccountLine label={t`Conta para troco atual`} account={current} />}
      <SelectFormField<BankDetailsValues>
        name="operationTypeValue"
        label={t`Tipo de Operação`}
        placeholder={t`Selecione`}
        options={fromConfig(operationConfig) ?? options(i18n, OPERATION_TYPES)}
        onValueChange={(value) => clear(clearedForOperation(value))}
      />
      {operation === PIX ? (
        <SelectFormField<BankDetailsValues>
          name="pixKeyTypeValue"
          label={t`Tipo de chave Pix`}
          placeholder={t`Selecione`}
          options={options(i18n, legalPerson ? LEGAL_PIX_KEY_TYPES : PIX_KEY_TYPES)}
          // LiquidationScheduleClosureRevision/index.tsx:81-88: fora de agência e conta, limpa os dados da conta.
          onValueChange={(value) => {
            if (value !== PIX_AGENCY_AND_ACCOUNT) clear(clearedForOperation(PIX));
          }}
        />
      ) : null}
      {keyType && keyType !== PIX_AGENCY_AND_ACCOUNT ? (
        mask ? (
          <MaskedFormField<BankDetailsValues> name="keyPix" label={keyLabel} kind={mask} />
        ) : (
          <TextFormField<BankDetailsValues> name="keyPix" label={keyLabel} {...(keyType === 'Email' ? { type: 'email' as const } : {})} />
        )
      ) : null}
      {bankData ? (
        <>
          <EntityPickerField<BankDetailsValues, BankItem>
            name="bankCode"
            displayName="bankCodeDisplay"
            label={t`Banco`}
            required
            source={isTransfer ? bankCompeSource : BANKS_ALL}
            placeholder={t`Buscar por código ou nome`}
            onSelect={(item, set) => set('bankIspb', item?.ispb ?? null)}
          />
          <SelectFormField<BankDetailsValues> name="type" label={t`Tipo de conta`} required placeholder={t`Selecione`} options={fromConfig(typeConfig) ?? options(i18n, legalPerson ? LEGAL_ACCOUNT_TYPES : NATURAL_ACCOUNT_TYPES)} />
          <TextFormField<BankDetailsValues> name="agency" label={t`Agência (sem o digito)`} placeholder={t`Ex: "1234"`} inputMode="numeric" />
          <TextFormField<BankDetailsValues> name="account" label={t`Conta`} inputMode="numeric" />
          <TextFormField<BankDetailsValues> name="accountDigit" label={t`Dígito da conta`} placeholder={t`Ex: "1"`} />
        </>
      ) : null}
    </>
  );
}
